import { useEffect, useRef, useState } from 'react';
import { api, type Santiye } from '../lib/api';
import { MusteriLogo } from '../marka';

/**
 * MÜŞTERİ SEÇİCİ — ray'ın tepesindeki logo, aynı zamanda geçiş kapısı.
 *
 * ⚠️ TEK MÜŞTERİDE DROPDOWN YOK. Saha çalışanı tek müşteride çalışır; ona
 *    açılır liste göstermek, cevabı belli bir soru sormaktır. Bu durumda
 *    bileşen düz bir logo çizer — tıklanamaz, ok işareti yok.
 *
 * ⚠️ GEÇİŞTEN SONRA TAM YENİLEME (`location.reload`), akıllı bir state
 *    tazeleme değil. Gerekçe: müşteri değişince ekrandaki HER ŞEY değişir —
 *    işler, kadro, üniteler, rapor, arşiv, hatta kullanıcının rolü (aynı kişi
 *    Nuh'ta şef, Akçansa'da mühendis olabilir). Tek tek geçersiz kılmaya
 *    çalışmak, bir listeyi unutup KARIŞIK VERİ göstermek demekti; iki
 *    müşterinin verisinin aynı ekranda görünmesi de tam olarak kaçındığımız
 *    şey. Yeniden yükleme kaba ama sonucu kesin.
 */
export function MusteriSecici({
  santiye,
  santiyeler,
  dar,
  temsilAltinda,
}: {
  santiye: Santiye;
  santiyeler: Santiye[];
  dar: boolean;
  temsilAltinda: boolean;
}) {
  const [acik, setAcik] = useState(false);
  const [gecisYapiliyor, setGecis] = useState<string | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!acik) return;
    const d = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setAcik(false);
    };
    document.addEventListener('mousedown', d);
    return () => document.removeEventListener('mousedown', d);
  }, [acik]);

  /*
   * Temsil altında geçiş KAPALI — sunucu da reddediyor (auth.controller
   * santiyeSec). Buradaki kapatma, sunucunun reddedeceği bir butonu
   * göstermemek için: kural iki yerde ayrı yazılmıyor, aynı kural
   * client'ta butonu söndürüyor, server'da isteği reddediyor.
   */
  const secilebilir = santiyeler.length > 1 && !temsilAltinda;

  async function gec(hedef: Santiye) {
    if (hedef.siteId === santiye.siteId) return setAcik(false);
    setGecis(hedef.siteId);
    setHata(null);
    try {
      await api.post('/auth/santiye-sec', { siteId: hedef.siteId });
      window.location.reload();
    } catch (e) {
      setGecis(null);
      setHata(e instanceof Error ? e.message : 'Geçiş yapılamadı.');
    }
  }

  /* ---------------- DAR RAY ---------------- */

  if (dar) {
    return (
      <div className="relative flex justify-center px-2 pb-2" ref={ref}>
        <button
          onClick={() => secilebilir && setAcik((v) => !v)}
          disabled={!secilebilir}
          title={secilebilir ? `${santiye.musteriAd} — değiştir` : santiye.musteriAd}
          className="flex h-9 w-11 items-center justify-center rounded-md bg-white px-1 disabled:cursor-default"
        >
          <MusteriLogo kod={santiye.musteriKod} ad={santiye.musteriAd} yukseklik="h-6" />
        </button>
        {acik && (
          <Liste
            santiye={santiye}
            santiyeler={santiyeler}
            gecisYapiliyor={gecisYapiliyor}
            onSec={gec}
            className="absolute top-full left-2 z-50 mt-1 w-56"
          />
        )}
      </div>
    );
  }

  /* ---------------- GENİŞ RAY ---------------- */

  return (
    <div className="relative mx-4 mb-4" ref={ref}>
      <button
        onClick={() => secilebilir && setAcik((v) => !v)}
        disabled={!secilebilir}
        className={`flex w-full items-center gap-3 rounded-(--radius-mod) bg-white/8 px-3 py-3 text-left ${
          secilebilir ? 'hover:bg-white/12' : 'cursor-default'
        }`}
      >
        <MusteriLogo
          kod={santiye.musteriKod}
          ad={santiye.musteriAd}
          yukseklik="h-8"
          className="shrink-0"
        />
        <div className="min-w-0 flex-1">
          <div className="truncate text-xs text-white/55">
            {santiye.konum ?? santiye.siteAd}
          </div>
        </div>
        {/* Ok YALNIZ birden fazla müşteri varsa — yoksa tıklanabilir görünüp
            hiçbir şey yapmayan bir öğe olurdu. */}
        {secilebilir && (
          <span className="shrink-0 text-xs text-white/45" aria-hidden>
            ▾
          </span>
        )}
      </button>

      {temsilAltinda && santiyeler.length > 1 && (
        <p className="mt-1 px-1 text-[11px] leading-snug text-white/40">
          Kullanıcı geçişi sırasında müşteri değiştirilemez.
        </p>
      )}
      {hata && <p className="mt-1 px-1 text-[11px] text-crit">{hata}</p>}

      {acik && (
        <Liste
          santiye={santiye}
          santiyeler={santiyeler}
          gecisYapiliyor={gecisYapiliyor}
          onSec={gec}
          className="absolute top-full right-0 left-0 z-50 mt-1"
        />
      )}
    </div>
  );
}

function Liste({
  santiye,
  santiyeler,
  gecisYapiliyor,
  onSec,
  className,
}: {
  santiye: Santiye;
  santiyeler: Santiye[];
  gecisYapiliyor: string | null;
  onSec: (s: Santiye) => void;
  className: string;
}) {
  return (
    <div
      className={`overflow-hidden rounded-(--radius-mod) border border-line bg-surface shadow-(--shadow-mod) shadow-lg ${className}`}
    >
      {santiyeler.map((s) => {
        const aktif = s.siteId === santiye.siteId;
        return (
          <button
            key={s.siteId}
            onClick={() => onSec(s)}
            disabled={gecisYapiliyor != null}
            className={`flex w-full items-center gap-2.5 border-b border-line/60 px-3 py-2.5 text-left last:border-b-0 ${
              aktif ? 'bg-navy/5' : 'hover:bg-ground'
            }`}
          >
            {/* Listedeki logolar zaten beyaz zeminde — çerçevesiz. */}
            <MusteriLogo
              kod={s.musteriKod}
              ad={s.musteriAd}
              yukseklik="h-6"
              className="shrink-0 !px-0"
            />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-medium text-ink">{s.musteriAd}</span>
              <span className="block truncate text-[11px] text-wait">
                {s.konum ?? s.siteAd}
              </span>
            </span>
            {gecisYapiliyor === s.siteId ? (
              <span className="shrink-0 text-[11px] text-wait">geçiliyor…</span>
            ) : (
              aktif && (
                <span className="shrink-0 text-xs text-ok" aria-label="Aktif">
                  ✓
                </span>
              )
            )}
          </button>
        );
      })}
    </div>
  );
}
